import React, { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { apiService } from "../services/api";
import { User, AuthResponse } from "../types";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (auth: AuthResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is logged in on mount
  const { isLoading: profileLoading } = useQuery(
    "profile",
    apiService.getProfile,
    {
      retry: false,
      enabled: !!localStorage.getItem("token"),
      onSuccess: (response) => {
        if (response.data?.success && response.data.data) {
          setUser(response.data.data);
          localStorage.setItem("user", JSON.stringify(response.data.data));
        }
      },
      onError: () => {
        // Clear invalid token
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      },
    },
  );

  useEffect(() => {
    // Check for stored user data
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        localStorage.removeItem("user");
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!profileLoading) {
      setIsLoading(false);
    }
  }, [profileLoading]);

  const login = (auth: AuthResponse) => {
    localStorage.setItem("token", auth.token);
    localStorage.setItem("user", JSON.stringify(auth.user));
    setUser(auth.user);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
